/**
 * @todo Remove all token refresh functionality from api requests and instead have the refreshes taken place
 * one minute before expiry. This cannot be done before we've addressed the issue of the worker's initial tab being closed.
 */

import { getApiDomain } from '@/mw-components/helpers/functions';
import { DEFAULT_HEADERS } from './constants';
import { Headers as RequestHeaders, Method, Methods } from './types';
import { ENDPOINTS } from './constants';
import { routerInstance, store } from '@/mw-components/store';
import { RefreshAdminTokenDTO, RefreshAdminTokenRequest } from '@/mw-components/types/auth';
import { AuthActionTypes } from '@/mw-components/store/modules/auth/action-types';
import { DashboardAppActionTypes } from '@/mw-components/store/modules/dashboard-app/action-types';

import storage from '@/mw-components/store/storage'; // Adding so we can store isRefreshingToken in local storage instead of state

type retry = {
  maxRetries: number;
};

type xhrOptions = {
  retry?: retry;
};

class MwApiBase {
  apiDomain: string;

  constructor(mode: string = 'production') {
    this.apiDomain = this.getApiDomain(mode);
  }

  async xhr<SendType, ResponseType>(
    method: Method,
    data: SendType,
    path: string,
    requiresAuth: boolean = true,
  ): Promise<ResponseType> {
    const request = async (): Promise<ResponseType> => {
      try {
        return await this.fetchRequest<SendType, ResponseType>(method, data, path, requiresAuth);
      } catch (error) {
        throw error;
      }
    };

    // We can look to remove this now the token is fetched on a timer and should never be refreshed on a request again.
    // BUT NOT YET!
    // Token refreshes still happen on request if the token has expired. This will happen, as the token only last 10 minutes.
    // And we also need to consider what will happen when someone closes the tab listening for the worker's messages.
    if (storage.get('isRefreshingToken') && !path.includes('/client/token')) {
      // We're refreshing the token - keep checking until it's complete
      return new Promise((resolve) => {
        const intervalId = setInterval(() => {
          if (!storage.get('isRefreshingToken')) {
            clearInterval(intervalId);
            resolve(this.fetchRequest<SendType, ResponseType>(method, data, path, requiresAuth));
          }
        }, 100);
      });
    } else {
      return request();
    }
  }

  async fetchRequest<SendType, ResponseType>(
    method: Method,
    data: SendType,
    path: string,
    requiresAuth: boolean = true,
  ): Promise<ResponseType> {
    const response = await fetch(`${this.apiDomain}${path}`, {
      method,
      body: this.getRequestBody<SendType>(data),
      headers: this.getRequestHeaders<SendType>(data, requiresAuth),
    });

    const isJson = response.headers.get('content-type')?.includes('application/json');

    const tokenExpired = response.headers.get('token-expired');

    if (response.ok) {
      storage.set('lastActivity', Date.now());
      return isJson ? await response.json() : await response.blob();

      // If the response is a 401, we need to refresh the token. In the time the original request was made, other will also have been made, though,
      // - we don't want them refreshing the token as well. we might already be refreshing the token, so check that's not the case.
    } else if (
      response.status === 401 &&
      !path.includes('/client/token') &&
      !storage.get('isRefreshingToken') &&
      tokenExpired
    ) {
      storage.set('isRefreshingToken', true);
      // Refresh the token
      return new Promise((resolve) => {
        this.tokenRefresh()
          .then(() => {
            return resolve(
              this.fetchRequest<SendType, ResponseType>(method, data, path, requiresAuth),
            );
          })
          .catch(() => {
            store.dispatch(`auth/${AuthActionTypes.LOGOUT}`);
            store.dispatch(`dashboardApp/${DashboardAppActionTypes.LOGOUT}`);
            // @ts-ignore
            routerInstance.router.push({
              // @ts-ignore
              name: routerInstance.paths.login.name,
            });
          });
      });
      // Catch the original requests that were sent out before the first 401 and add them to the queue
    } else if (response.status === 401 && !path.includes('/client/token') && tokenExpired) {
      return new Promise((resolve) => {
        return resolve(this.fetchRequest<SendType, ResponseType>(method, data, path, requiresAuth));
      });
    } else {
      throw await response.json();
    }
  }

  async tokenRefresh() {
    if (!(storage.get('token') && storage.get('refreshToken'))) {
      throw new Error('Token is empty');
    }

    const model: RefreshAdminTokenRequest = {
      Token: storage.get('token'),
      RefreshToken: storage.get('refreshToken'),
    };

    return await this.AuthenticateRefresh(model).then((value) => {
      if (value !== undefined) {
        const { token, refreshToken } = value;
        storage.set('token', token);
        storage.set('refreshToken', refreshToken);
        storage.set('isRefreshingToken', false);
      } else {
        throw new Error('Refresh token endpoint failed: undefined response');
      }
    });
  }

  AuthenticateRefresh = (model: RefreshAdminTokenRequest) =>
    this.xhr<RefreshAdminTokenRequest, RefreshAdminTokenDTO>(
      Methods.Post,
      model,
      ENDPOINTS.AuthenticateRefresh,
    );

  getRequestBody<SendType>(data: SendType) {
    // @ts-ignore
    return data instanceof FormData
      ? data
      : Object.keys(data).length !== 0
      ? JSON.stringify(data)
      : undefined;
  }

  getRequestHeaders<SendType>(data: SendType, requiresAuth: boolean): Headers {
    const requestHeaders = new Headers({ ...DEFAULT_HEADERS });
    if (data instanceof FormData) {
      requestHeaders.delete(RequestHeaders.ContentType);
    }
    if (requiresAuth) {
      requestHeaders.set(RequestHeaders.Authorization, `Bearer ${storage.get('token')}`);
    }
    return requestHeaders;
  }

  //TODO: Update this to work with Service Discovery
  getApiDomain(mode: string) {
    switch (mode) {
      case 'localdev':
        return 'https://localhost:5001';
      default:
        return getApiDomain();
    }
  }
}

export default MwApiBase;
